import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { PhoneCall, MessageSquareReply, BarChart3, Sunrise, BookOpen, ShieldCheck, ArrowRight, Printer, ChevronDown, X, Loader2, MailCheck, Volume2, Pause } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Industry template — duplicate this config for carsales / legal / B2B / helpdesk.
export const INDUSTRY_CONFIG = {
  gym: {
    brand: 'Gym & Wellness 360',
    tagline: 'Every call graded. Every lead chased. Every dollar counted.',
    sub: 'CallSense by Yabbit VoiceFabric turns your club’s phone calls into graded coaching, revived deals and booked tours — automatically.',
    crm: 'LeadDesk CRM (GymSales)',
    dealNoun: 'membership',
    defaultMonthly: 89,
    defaultMonths: 12,
    defaultRevivable: 60,
    creds: [['Manager', 'demo.admin@gw360.demo'], ['Supervisor', 'demo.supervisor@gw360.demo'], ['Rep', 'demo.mia@gw360.demo']],
    features: [
      { icon: BookOpen, title: 'AI Call Grading', body: 'Every sales call is transcribed and graded against your own criteria — openers, needs discovery, close attempts — with coaching feedback the rep sees the same day.' },
      { icon: ShieldCheck, title: 'Revivable Deals Queue', body: 'The AI spots deals that didn’t close but aren’t dead — ranked by confidence and freshness — so reps call the hottest ones back first.' },
      { icon: MessageSquareReply, title: 'SMS Playbooks + Reply Inbox', body: 'One-tap SMS from call playbooks. Replies land in a live inbox with a 15-minute countdown, supervisor nudges at 15 and 60 minutes, and one-tap templated answers.' },
      { icon: Sunrise, title: '6am Morning Sweep', body: 'Each morning we scan your CRM for calls due today, match each lead to their real call history, and hand every rep a playbook-backed call list.' },
      { icon: BarChart3, title: 'Club Summary + Discrepancy Report', body: 'Your CRM report rebuilt from actual call transcripts — contacts, appointments, close ratios — side by side with what got logged, so nothing slips.' },
      { icon: PhoneCall, title: 'Supervisor Command Centre', body: 'Morning digests, per-rep response times with SLIPPING flags, dropped-lead rescue that auto-queues ignored replies back into the call list.' },
    ],
    proof: [
      { n: '160+', l: 'calls graded monthly per club' },
      { n: '15 min', l: 'reply SLA with auto-escalation' },
      { n: '24%', l: 'close ratio measured from transcripts' },
      { n: '6am', l: 'daily CRM call-list sweep' },
    ],
  },
  carsales: {
    brand: 'Velocity Motors Group',
    tagline: 'Every enquiry graded. Every test drive chased. Every deal counted.',
    sub: 'CallSense turns your dealership’s phone calls into graded coaching, revived deals and booked test drives — automatically.',
    crm: 'your dealer CRM',
    dealNoun: 'vehicle',
    defaultMonthly: 780,
    defaultMonths: 48,
    defaultRevivable: 35,
    creds: [['Manager', 'demo.admin@vmg.demo'], ['Salesperson', 'demo.ryan@vmg.demo'], ['Salesperson', 'demo.sasha@vmg.demo']],
    features: [
      { icon: BookOpen, title: 'AI Call Grading', body: 'Every sales enquiry is transcribed and graded — qualification, trade-in discovery, test-drive close attempts — with same-day coaching for each salesperson.' },
      { icon: ShieldCheck, title: 'Revivable Deals Queue', body: 'Buyers who didn’t sign but aren’t gone — ranked by intent and freshness so your team calls the hottest prospects back first.' },
      { icon: MessageSquareReply, title: 'SMS Playbooks + Reply Inbox', body: 'One-tap SMS follow-ups after every enquiry. Replies land in a live inbox with a 15-minute countdown and supervisor escalation.' },
      { icon: Sunrise, title: '6am Morning Sweep', body: 'Every morning we scan your CRM for follow-ups due today and hand each salesperson a playbook-backed call list.' },
      { icon: BarChart3, title: 'Showroom Summary + Discrepancy Report', body: 'Your CRM’s activity report rebuilt from actual transcripts — enquiries, test drives booked, close ratios — beside what got logged.' },
      { icon: PhoneCall, title: 'Sales Manager Command Centre', body: 'Morning digests, per-salesperson response times with SLIPPING flags, and dropped-lead rescue back into the call list.' },
    ],
    proof: [
      { n: '100%', l: 'of enquiry calls graded' },
      { n: '15 min', l: 'reply SLA with auto-escalation' },
      { n: '6am', l: 'daily CRM follow-up sweep' },
      { n: '0', l: 'leads lost to unlogged calls' },
    ],
  },
  helpdesk: {
    brand: 'Apex IT Services',
    tagline: 'Every ticket call graded. Every promise kept. Every renewal protected.',
    sub: 'CallSense turns your helpdesk and account-management calls into graded QA, churn-risk alerts and renewal playbooks — automatically.',
    crm: 'your ticketing system',
    dealNoun: 'contract',
    defaultMonthly: 1500,
    defaultMonths: 12,
    defaultRevivable: 12,
    creds: [['Manager', 'demo.admin@apex.demo'], ['Engineer', 'demo.dev@apex.demo'], ['Engineer', 'demo.holly@apex.demo']],
    features: [
      { icon: BookOpen, title: 'AI Call QA', body: 'Every support call graded against your service standards — greeting, ownership, resolution confirmation — with coaching for each engineer.' },
      { icon: ShieldCheck, title: 'At-Risk Account Queue', body: 'The AI hears frustration, unresolved promises and churn signals — and queues those accounts for a manager callback before renewal time.' },
      { icon: MessageSquareReply, title: 'SMS Updates + Reply Inbox', body: 'Ticket-status SMS with one tap. Client replies land in a live inbox with a countdown so nobody waits on hold twice.' },
      { icon: Sunrise, title: '6am Morning Sweep', body: 'Each morning we scan your ticket system for follow-ups due today and hand every engineer a context-sheet call list.' },
      { icon: BarChart3, title: 'Service Summary + Discrepancy Report', body: 'SLA performance rebuilt from actual call transcripts — first-response, resolution confirmations — beside what got ticketed.' },
      { icon: PhoneCall, title: 'Service Manager Command Centre', body: 'Morning digests, per-engineer response times with SLIPPING flags, and escalation when a client reply sits unanswered.' },
    ],
    proof: [
      { n: '100%', l: 'of support calls QA graded' },
      { n: '15 min', l: 'client reply SLA' },
      { n: '24h', l: 'churn-risk alert window' },
      { n: '6am', l: 'daily ticket follow-up sweep' },
    ],
  },
};

const ListenButton = ({ vertical }) => {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef = React.useRef(null);
  const toggle = () => {
    if (!audioRef.current) {
      setLoading(true);
      const a = new Audio(`${API}/demo/narration/${vertical}.mp3`);
      a.onended = () => setPlaying(false);
      a.oncanplay = () => setLoading(false);
      a.onerror = () => { setLoading(false); setPlaying(false); };
      audioRef.current = a;
    }
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play().catch(() => setPlaying(false)); setPlaying(true); }
  };
  return (
    <button onClick={toggle}
      className="border border-slate-300 text-slate-700 font-semibold px-5 py-3 rounded-full inline-flex items-center gap-2 hover:border-teal-500 hover:text-slate-900 no-print"
      data-testid="demo-listen-btn">
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : playing ? <Pause className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      {playing ? 'Pause' : 'Listen · 45 sec overview'}
    </button>
  );
};

const fmt = (n) => n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });

const DemoAccessModal = ({ cfg, vertical, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [state, setState] = useState('idle');
  const [msg, setMsg] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setState('sending');
    try {
      const r = await axios.post(`${API}/demo/request-access`, { ...form, vertical });
      setMsg(r.data.emailed
        ? 'Check your inbox — your one-click demo link and password are on their way.'
        : 'Request received! Our team will send your demo credentials shortly.');
      setState('sent');
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Something went wrong — please try again.');
      setState('error');
    }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 no-print" onClick={onClose} data-testid="demo-access-modal">
      <div className="bg-white border border-teal-300 shadow-xl rounded-2xl p-7 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <p className="font-bold text-xl">Get your live demo access</p>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="demo-access-close"><X className="w-5 h-5" /></button>
        </div>
        {state === 'sent' ? (
          <div className="mt-5 text-center py-6" data-testid="demo-access-success">
            <MailCheck className="w-10 h-10 text-teal-600 mx-auto mb-3" />
            <p className="font-semibold">{msg}</p>
            <p className="text-sm text-slate-500 mt-2">The guided tour starts automatically on your first login.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3">
            <p className="text-sm text-slate-600">Leave your details and we'll email you the {cfg.brand} demo login — fully loaded, no obligation.</p>
            {[['name', 'Full name', 'text'], ['email', 'Work email', 'email'], ['phone', 'Phone number', 'tel']].map(([k, label, type]) => (
              <label key={k} className="block">
                <span className="text-xs font-semibold text-teal-600 uppercase tracking-wide">{label}</span>
                <input type={type} required value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="mt-1 w-full bg-slate-100 border border-slate-200 border border-slate-200 rounded-lg px-3 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid={`demo-access-${k}`} />
              </label>
            ))}
            {state === 'error' && <p className="text-sm text-red-400" data-testid="demo-access-error">{msg}</p>}
            <button type="submit" disabled={state === 'sending'}
              className="w-full bg-teal-600 text-slate-900 font-bold py-3 rounded-full hover:bg-teal-300 inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="demo-access-submit">
              {state === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Email me the demo login
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const ROI_CONFIG = {
  gym: {
    inputs: [
      { key: 'monthly', label: 'Monthly membership value ($)', def: 89, step: 5 },
      { key: 'months', label: 'Avg retention (months)', def: 12, step: 1 },
      { key: 'revivable', label: 'Revivable membership deals / month', def: 60, step: 5 },
    ],
    compute: (v, ratio) => {
      const dealValue = v.monthly * v.months;
      const won = v.revivable * ratio;
      return {
        boxes: [
          { val: dealValue, label: 'value per membership (annualised)' },
          { val: won * dealValue, label: `revived revenue / month (${won.toFixed(1)} memberships)`, teal: true },
          { val: won * dealValue * 12, label: 'revived revenue / year', hi: true },
        ],
      };
    },
    note: 'Assumptions: revived-deal ratio applied to AI-detected revivable deals only — excludes uplift from faster reply times, morning call lists and coaching gains.',
  },
  carsales: {
    inputs: [
      { key: 'gp', label: 'Monthly GP value per car ($)', def: 2500, step: 100 },
      { key: 'deals', label: 'Avg deals per salesperson / month', def: 12, step: 1 },
      { key: 'revivable', label: 'Revivable deals with coaching / month', def: 35, step: 5 },
    ],
    compute: (v, ratio) => {
      const won = v.revivable * ratio;
      const gain = won * v.gp;
      return {
        boxes: [
          { val: v.gp, label: 'gross profit per car' },
          { val: gain, label: `extra GP / month (${won.toFixed(1)} cars — on top of ${v.deals}/salesperson today)`, teal: true },
          { val: gain * 12, label: 'extra GP / year', hi: true },
        ],
      };
    },
    note: 'Assumptions: revived ratio applied to AI-detected revivable deals with coaching only — excludes GP uplift from faster enquiry replies and morning follow-up lists.',
  },
  helpdesk: {
    inputs: [
      { key: 'tickets', label: 'Monthly ticket qty per engineer', def: 250, step: 10 },
      { key: 'contract', label: 'Avg contract value ($/month)', def: 1500, step: 100 },
      { key: 'retention', label: 'Avg customer retention (months)', def: 36, step: 3 },
      { key: 'revivable', label: 'Revivable opportunities (sales + mgmt) / month', def: 12, step: 1 },
    ],
    compute: (v, ratio) => {
      const ltv = v.contract * v.retention;
      const won = v.revivable * ratio;
      const gain = won * ltv;
      return {
        boxes: [
          { val: ltv, label: 'lifetime value per customer' },
          { val: gain, label: `revenue protected / month (${won.toFixed(1)} saves)`, teal: true },
          { val: gain * 12, label: 'revenue protected / year', hi: true },
        ],
        extra: `QA covers all ~${v.tickets} tickets per engineer each month — no extra QA headcount.`,
      };
    },
    note: 'Assumptions: save ratio applied to AI-detected revivable sales & management opportunities only — excludes churn avoided through faster response times.',
  },
};

const RoiCalculator = ({ vertical }) => {
  const rcfg = ROI_CONFIG[vertical] || ROI_CONFIG.gym;
  const [vals, setVals] = useState(() => Object.fromEntries(rcfg.inputs.map((i) => [i.key, i.def])));
  const [mode, setMode] = useState('conservative');
  const ratio = mode === 'conservative' ? 0.08 : 0.2;
  const out = rcfg.compute(vals, ratio);
  return (
    <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-6 " data-testid="roi-calculator">
      <div className={`grid gap-4 mb-5 ${rcfg.inputs.length > 3 ? 'grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'}`}>
        {rcfg.inputs.map((inp) => (
          <label key={inp.key} className="block">
            <span className="text-xs font-semibold text-teal-600 uppercase tracking-wide">{inp.label}</span>
            <input type="number" value={vals[inp.key]} step={inp.step} min={0}
              onChange={(e) => setVals({ ...vals, [inp.key]: Number(e.target.value) || 0 })}
              className="mt-1 w-full bg-slate-100 border border-slate-200 border border-slate-200 rounded-lg px-3 py-2 text-slate-900 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-teal-500"
              data-testid={`roi-input-${inp.key}`} />
          </label>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-5" data-testid="roi-mode-toggle">
        {[['conservative', 'Conservative · 8% revived'], ['aggressive', 'Aggressive · 20% revived']].map(([v, l]) => (
          <button key={v} onClick={() => setMode(v)}
            className={`text-sm font-semibold px-4 py-2 rounded-full transition-colors ${mode === v ? 'bg-teal-600 text-slate-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid={`roi-mode-${v}`}>{l}</button>
        ))}
      </div>
      <div className="grid sm:grid-cols-3 gap-4 text-center">
        {out.boxes.map((b, i) => (
          <div key={b.label} className={b.hi ? 'bg-teal-400/10 border border-teal-400/30 rounded-xl py-4' : 'bg-slate-100 border border-slate-200 rounded-xl py-4'}>
            <p className={`text-3xl font-bold ${b.teal || b.hi ? 'text-teal-600' : 'text-slate-900'}`} data-testid={`roi-box-${i}`}>{fmt(b.val)}</p>
            <p className={`text-xs mt-1 ${b.hi ? 'text-teal-700' : 'text-slate-500'}`}>{b.label}</p>
          </div>
        ))}
      </div>
      {out.extra && <p className="text-xs text-teal-600/80 font-semibold mt-3" data-testid="roi-extra">{out.extra}</p>}
      <p className="text-[11px] text-slate-400 mt-3">{rcfg.note}</p>
    </div>
  );
};

export default function DemoShowcasePage() {
  const { industry } = useParams();
  const vertical = INDUSTRY_CONFIG[industry] ? industry : 'gym';
  const cfg = INDUSTRY_CONFIG[vertical];
  const [showAccess, setShowAccess] = useState(false);
  const [zoomShot, setZoomShot] = useState(null);
  return (
    <div className="bg-slate-50 text-slate-900 min-h-screen" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="demo-showcase-page">
      {showAccess && <DemoAccessModal cfg={cfg} vertical={vertical} onClose={() => setShowAccess(false)} />}
      {zoomShot && (
        <div className="fixed inset-0 z-[110] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10 no-print cursor-zoom-out"
          onClick={() => setZoomShot(null)} data-testid="screenshot-lightbox">
          <figure className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={zoomShot.src} alt={zoomShot.title} className="w-full rounded-xl border border-slate-300 shadow-2xl" />
            <figcaption className="flex items-start justify-between gap-4 mt-3">
              <div>
                <p className="font-bold">{zoomShot.title}</p>
                <p className="text-sm text-slate-500">{zoomShot.cap}</p>
              </div>
              <button onClick={() => setZoomShot(null)} className="shrink-0 p-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-900" data-testid="lightbox-close">
                <X className="w-5 h-5" />
              </button>
            </figcaption>
          </figure>
        </div>
      )}
      <style>{`@media print {
        .no-print{display:none!important}
        .slide{min-height:auto!important;page-break-after:always;padding:24px!important}
        body{background:#fff}
        .slide, .slide *{color:#0f172a!important}
        .slide .text-teal-600, .slide .text-teal-600{color:#0d9488!important}
        .slide img{break-inside:avoid;border:1px solid #e2e8f0}
      }`}</style>

      {/* Slide 1 — hero */}
      <section className="slide min-h-screen flex flex-col justify-center px-8 sm:px-20 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-teal-500/10 blur-3xl" />
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo192.png" alt="Yabbit VoiceFabric" className="w-11 h-11 rounded-xl border border-slate-200 bg-white p-1 shadow-sm" onError={(e) => { e.target.style.display = 'none'; }} />
          <div>
            <p className="font-bold text-slate-900 leading-tight">Yabbit VoiceFabric</p>
            <p className="text-[11px] text-slate-500">AI call grading · coaching · revenue intelligence — live product demo</p>
          </div>
        </div>
        <p className="text-teal-600 font-bold tracking-[0.3em] uppercase text-sm mb-4">CallSense · Demo — {cfg.brand}</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold max-w-3xl leading-tight">{cfg.tagline}</h1>
        <p className="text-lg text-slate-600 max-w-2xl mt-5">{cfg.sub}</p>
        <p className="text-base font-semibold text-teal-700 max-w-2xl mt-4" data-testid="demo-superpowers-line">
          Let's give your team AI superpowers — to create amazing relationships and get the best outcomes for your business.
        </p>
        <div className="flex items-center gap-3 mt-8 no-print">
          <button onClick={() => setShowAccess(true)} className="bg-teal-600 text-white font-bold px-6 py-3 rounded-full inline-flex items-center gap-2 hover:bg-teal-500" data-testid="demo-cta-login">
            Open the live demo <ArrowRight className="w-4 h-4" />
          </button>
          <ListenButton vertical={vertical} />
          <button onClick={() => window.print()} className="border border-slate-300 text-slate-600 font-semibold px-6 py-3 rounded-full inline-flex items-center gap-2 hover:bg-slate-100" data-testid="demo-print-brochure">
            <Printer className="w-4 h-4" /> Save as brochure (PDF)
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-14 max-w-3xl">
          {cfg.proof.map((p) => (
            <div key={p.l}><p className="text-3xl font-bold text-teal-600">{p.n}</p><p className="text-xs text-slate-500 mt-1">{p.l}</p></div>
          ))}
        </div>
        <ChevronDown className="w-6 h-6 text-slate-300 mt-12 animate-bounce no-print" />
      </section>

      {/* Slide 1b — why: pain points, benefits, outcomes + the revenue question */}
      <section className="slide min-h-screen flex flex-col justify-center px-8 sm:px-20 py-16 bg-white border-y border-slate-100" data-testid="demo-value-section">
        <h2 className="text-lg font-bold text-teal-600 uppercase tracking-[0.2em] mb-2">Why teams switch it on</h2>
        <p className="text-2xl sm:text-3xl font-bold max-w-3xl mb-3">How much extra revenue could you make if every person on your team performed like your best?</p>
        <p className="text-sm text-slate-500 max-w-2xl mb-8">That's the only question that matters. Here's how VoiceFabric answers it.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-6xl">
          <div className="rounded-2xl border border-red-100 bg-red-50/60 p-6">
            <p className="text-xs font-bold uppercase tracking-wide text-red-600 mb-3">Pain points it removes</p>
            <ul className="text-sm text-slate-700 space-y-2.5 list-disc ml-4">
              <li>Leads that never get a follow-up call and quietly go cold</li>
              <li>No idea what's actually said on calls — coaching by guesswork</li>
              <li>Replies sitting unanswered while competitors call back first</li>
              <li>CRM reports that don't match what really happened on the phone</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-teal-100 bg-teal-50/60 p-6">
            <p className="text-xs font-bold uppercase tracking-wide text-teal-700 mb-3">Benefits from day one</p>
            <ul className="text-sm text-slate-700 space-y-2.5 list-disc ml-4">
              <li>Every call transcribed and graded against your own playbook</li>
              <li>Same-day coaching feedback every rep can act on</li>
              <li>A 15-minute reply SLA with automatic supervisor escalation</li>
              <li>Revivable deals ranked overnight so mornings start with the hottest calls</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-6">
            <p className="text-xs font-bold uppercase tracking-wide text-emerald-700 mb-3">Outcomes to expect</p>
            <ul className="text-sm text-slate-700 space-y-2.5 list-disc ml-4">
              <li>More booked appointments from the same lead flow</li>
              <li>Deals rescued that would have been written off</li>
              <li>Faster ramp-up for new team members</li>
              <li>Managers coaching from evidence, not opinions</li>
            </ul>
          </div>
        </div>
        <button onClick={() => setShowAccess(true)} className="mt-8 bg-teal-600 text-white font-bold px-6 py-3 rounded-full inline-flex items-center gap-2 hover:bg-teal-500 w-fit no-print" data-testid="demo-value-cta">
          See it on your own numbers <ArrowRight className="w-4 h-4" />
        </button>
      </section>

      {/* Slide 2 — features (screens, what they do and mean) */}
      <section className="slide min-h-screen flex flex-col justify-center px-8 sm:px-20 py-16">
        <h2 className="text-lg font-bold text-teal-600 uppercase tracking-[0.2em] mb-2">The screens & what they mean</h2>
        <p className="text-3xl font-bold mb-10 max-w-2xl">Six systems working while your team sells.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="demo-features">
          {cfg.features.map((f) => (
            <div key={f.title} className="bg-white border border-slate-200 shadow-sm rounded-2xl p-5 hover:border-teal-400/40 transition-colors">
              <f.icon className="w-6 h-6 text-teal-600 mb-3" />
              <p className="font-bold mb-1.5">{f.title}</p>
              <p className="text-sm text-slate-600 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Slide 2b — real product screenshots */}
      <section className="slide min-h-screen flex flex-col justify-center px-8 sm:px-20 py-16">
        <h2 className="text-lg font-bold text-teal-600 uppercase tracking-[0.2em] mb-2">The real product</h2>
        <p className="text-3xl font-bold mb-2 max-w-2xl">These aren't mock-ups. This is the live platform.</p>
        <p className="text-sm text-slate-500 mb-8">Captured from the live demo environment — all names, numbers and clubs are anonymised demo data.</p>
        <div className="grid sm:grid-cols-2 gap-6 max-w-5xl" data-testid="demo-screenshots">
          {[
            { src: '/demo-shots/dashboard.jpg', title: 'Executive Dashboard', cap: 'Scorecard, cost savings, revivable deals and the SMS reply inbox — one screen, every morning.' },
            { src: '/demo-shots/calls.jpg', title: 'Graded Calls', cap: 'Every call transcribed and scored against your own criteria, with coaching the rep sees same-day.' },
            { src: '/demo-shots/sms-inbox.jpg', title: 'SMS Inbox', cap: 'Every message in and out, with the 15-minute reply countdown and one-tap templates.' },
            { src: '/demo-shots/club-summary.jpg', title: 'Summary Report', cap: 'Your monthly report rebuilt from actual transcripts, side by side with what got logged.' },
          ].map((s) => (
            <figure key={s.src} onClick={() => setZoomShot(s)}
              className="bg-white border border-slate-200 shadow-sm rounded-2xl overflow-hidden hover:border-teal-400/40 transition-colors cursor-zoom-in"
              data-testid={`demo-shot-${s.src.split('/').pop().split('.')[0]}`}>
              <img src={s.src} alt={s.title} className="w-full aspect-[16/9] object-cover object-top border-b border-slate-200" />
              <figcaption className="p-4">
                <p className="font-bold text-sm">{s.title}</p>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{s.cap}</p>
                <p className="text-[10px] text-teal-600/70 font-semibold mt-1.5 uppercase tracking-wide no-print">Click to enlarge</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Slide 3 — CRM integration + discrepancy story */}
      <section className="slide min-h-screen flex flex-col justify-center px-8 sm:px-20 py-16">
        <h2 className="text-lg font-bold text-teal-600 uppercase tracking-[0.2em] mb-2">CRM integration — {cfg.crm}</h2>
        <p className="text-3xl font-bold max-w-2xl mb-6">Your CRM says one thing. The phone lines say another. Now you can see both.</p>
        <div className="grid lg:grid-cols-2 gap-6 max-w-4xl">
          <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-6">
            <p className="font-bold text-teal-600 mb-2">Two-way sync</p>
            <ul className="text-sm text-slate-600 space-y-2 list-disc ml-4">
              <li>Every SMS sent or received is written back into the CRM contact’s notes</li>
              <li>Morning sweep reads scheduled calls + assigned reps straight from the CRM</li>
              <li>“Done” in our call list logs a timestamped note in the CRM automatically</li>
            </ul>
          </div>
          <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-6">
            <p className="font-bold text-teal-600 mb-2">The discrepancy report</p>
            <p className="text-sm text-slate-600 leading-relaxed">The Club Summary rebuilds your CRM’s monthly report from <b className="text-slate-900">actual call transcripts</b>: phone-ins, calls out, spoke-to vs no-answer — side by side with what staff logged. The gap between the rows is your untracked revenue.</p>
            <div className="mt-4 text-xs bg-slate-100 border border-slate-200 rounded-lg p-3 font-mono text-slate-600">
              <p>VoiceFabric (transcripts) · out 396 · spoke 162</p>
              <p className="text-amber-300">CRM (logged) &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;· out 118 · spoke &nbsp;71</p>
              <p className="text-red-400">→ 278 calls never logged by staff</p>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 4 — ROI calculator */}
      <section className="slide min-h-screen flex flex-col justify-center px-8 sm:px-20 py-16">
        <h2 className="text-lg font-bold text-teal-600 uppercase tracking-[0.2em] mb-2">ROI — what a revived deal is worth</h2>
        <p className="text-3xl font-bold max-w-2xl mb-8">Put your own numbers in. Be conservative. It still pays.</p>
        <div className="max-w-3xl"><RoiCalculator vertical={vertical} /></div>
      </section>

      {/* Slide 5 — CTA / demo access */}
      <section className="slide min-h-[70vh] flex flex-col justify-center px-8 sm:px-20 py-16">
        <h2 className="text-3xl sm:text-4xl font-bold max-w-2xl">See it live — the {cfg.brand} demo is fully loaded.</h2>
        <p className="text-slate-600 mt-4 max-w-xl">Graded calls, revivable deal queues, the SMS inbox, morning call lists and the Club Summary — all with realistic demo data that never goes stale.</p>
        <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-6 mt-8 max-w-md text-sm" data-testid="demo-credentials">
          <p className="font-bold text-teal-600 mb-2">Demo access</p>
          {cfg.creds.map(([role, email]) => (
            <p key={email} className="text-slate-600 font-mono text-xs">{role} · {email}</p>
          ))}
          <button onClick={() => setShowAccess(true)}
            className="mt-4 w-full bg-teal-600 text-slate-900 font-bold py-2.5 rounded-full hover:bg-teal-300 no-print"
            data-testid="demo-get-access-btn">
            Get instant access — emailed in seconds
          </button>
          <p className="text-slate-400 text-[11px] mt-2">Leave your name, email and phone — we'll email you the password straight away.</p>
        </div>
        <p className="text-[11px] text-slate-300 mt-10">Template-ready: this demo duplicates for car sales, legal, B2B equipment and IT helpdesk verticals — same engine, different playbooks.</p>
      </section>
    </div>
  );
}
